import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { format, getDaysInMonth, getDay } from 'date-fns';
import { useSelector } from 'react-redux';
import { selectCompanyName } from '@/store/slices/companySlice';
import { selectActiveCompanyId } from '@/store/slices/workspaceSlice';
import { useMonthlyAllAttendance } from '@/api/queries/attendance.queries';
import { useDepartments, useEmployees } from '@/api/queries/employee.queries';
import { buildMonthlyGridRows, generateCustomReport } from '@/lib/ReportGenerator';
import { cn } from '@/lib/cn';

const DAY_ABBR = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const STATUS_CONFIG = {
  P:  { bg: 'bg-green-500',  text: 'text-white',                       label: 'Present'    },
  HP: { bg: 'bg-yellow-400', text: 'text-white',                       label: 'Half Day'   },
  A:  { bg: 'bg-red-500',    text: 'text-white',                       label: 'Absent'     },
  L:  { bg: 'bg-blue-500',   text: 'text-white',                       label: 'Leave'      },
  H:  { bg: 'bg-purple-500', text: 'text-white',                       label: 'Holiday'    },
  S:  { bg: 'bg-gray-300',   text: 'text-gray-600 dark:text-gray-400', label: 'Sunday/Off' },
};

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function StatusCell({ code, dayTimes }) {
  const cfg = STATUS_CONFIG[code] || STATUS_CONFIG.S;
  const hasPunches = dayTimes?.punches?.length > 0;
  const isMultiple = dayTimes?.punch_count > 2;

  const tooltip = hasPunches
    ? dayTimes.punches.join(' · ')
    : cfg.label;

  return (
    <div className="relative group flex flex-col items-center gap-0.5">
      <div
        className={cn('w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold mx-auto cursor-default select-none', cfg.bg, cfg.text)}
        title={tooltip}>
        {code || '—'}
      </div>
      {isMultiple && (
        <div className="text-[8px] font-bold text-[var(--accent)] leading-none">{dayTimes.punch_count}×</div>
      )}
      {/* Tooltip */}
      {hasPunches && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-50 hidden group-hover:block pointer-events-none">
          <div className="bg-gray-900 text-white text-[10px] rounded px-2 py-1.5 whitespace-nowrap shadow-lg">
            {dayTimes.punches.map((p, i) => <div key={i}>{p}</div>)}
            {dayTimes.ot_min > 0 && (
              <div className="mt-1 pt-1 border-t border-gray-700 text-amber-300 font-semibold">
                OT: {Math.floor(dayTimes.ot_min/60)}h {dayTimes.ot_min%60}m
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function buildPrintHtml({ employees, dayHeaders, daysInMonth, year, month, companyName }) {
  const monthName = MONTHS[month - 1];
  const statusSymbol = { P: '●', HP: '◑', A: '○', L: 'L', H: 'H', S: '–' };

  const headerCells = dayHeaders.map(({ day, abbr, isWeekend }) =>
    `<th style="min-width:22px;background:${isWeekend ? '#fff5f5' : '#f8f9fa'};color:${isWeekend ? '#e53e3e' : '#666'};font-size:9px;padding:3px 2px;border:1px solid #ddd;text-align:center">
      <div>${day}</div><div style="opacity:.7">${abbr[0]}</div>
    </th>`).join('');

  const rows = employees.map(emp => {
    const dayCells = dayHeaders.map(({ day }) => {
      const code = emp.days?.[String(day)] || 'S';
      const cfg = STATUS_CONFIG[code];
      const colors = { P: '#22c55e', HP: '#facc15', A: '#ef4444', L: '#3b82f6', H: '#a855f7', S: '#d1d5db' };
      return `<td style="text-align:center;border:1px solid #eee;padding:2px">
        <span style="display:inline-block;width:18px;height:18px;border-radius:50%;background:${colors[code]||colors.S};color:white;font-size:8px;font-weight:bold;line-height:18px;text-align:center">${code}</span>
      </td>`;
    }).join('');

    const summaryCells = ['P','HP','A','L','H','S'].map(s =>
      `<td style="text-align:center;border:1px solid #eee;padding:2px;font-size:9px;font-weight:bold">${emp.summary?.[s]??0}</td>`
    ).join('');

    return `<tr>
      <td style="border:1px solid #eee;padding:3px 5px;white-space:nowrap;font-size:9px">
        <strong>${emp.name}</strong><br/>
        <span style="color:#666;font-size:8px">${emp.designation||emp.department||''}</span><br/>
        <span style="color:#999;font-size:7px;font-family:monospace">${emp.employee_code}</span>
      </td>
      ${dayCells}
      ${summaryCells}
      <td style="text-align:center;border:1px solid #eee;padding:2px;font-size:9px;color:#666">${emp.summary?.OT||'0h'}</td>
    </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Monthly Attendance — ${monthName} ${year} — ${companyName}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 8mm; font-size: 10px; color: #111; }
  @page { size: A3 landscape; margin: 8mm; }
  h2 { font-size: 14px; margin: 0 0 2px; }
  .meta { color: #666; font-size: 10px; margin-bottom: 8px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f8f9fa; border: 1px solid #ddd; padding: 3px 2px; font-size: 9px; text-transform: uppercase; }
  .legend { display: flex; gap: 12px; margin-bottom: 6px; flex-wrap: wrap; }
  .legend span { display: inline-flex; align-items: center; gap: 4px; font-size: 9px; }
  .dot { display: inline-block; width: 12px; height: 12px; border-radius: 50%; }
</style>
</head>
<body>
<h2>${companyName} — Monthly Attendance Report</h2>
<div class="meta">${monthName} ${year} · ${employees.length} employees · Generated ${new Date().toLocaleString('en-IN')}</div>
<div class="legend">
  <span><span class="dot" style="background:#22c55e"></span> P = Present</span>
  <span><span class="dot" style="background:#facc15"></span> HP = Half Day</span>
  <span><span class="dot" style="background:#ef4444"></span> A = Absent</span>
  <span><span class="dot" style="background:#3b82f6"></span> L = Leave</span>
  <span><span class="dot" style="background:#a855f7"></span> H = Holiday</span>
  <span><span class="dot" style="background:#d1d5db"></span> S = Sunday/Off</span>
</div>
<table>
  <thead>
    <tr>
      <th style="text-align:left;min-width:130px;padding:4px 5px">Employee</th>
      ${headerCells}
      <th style="background:#f0fff4;color:#22a35a;font-size:9px;padding:3px">P</th>
      <th style="font-size:9px;padding:3px">HP</th>
      <th style="background:#fff5f5;color:#e53e3e;font-size:9px;padding:3px">A</th>
      <th style="background:#eff6ff;color:#3b82f6;font-size:9px;padding:3px">L</th>
      <th style="font-size:9px;padding:3px">H</th>
      <th style="font-size:9px;padding:3px">S</th>
      <th style="font-size:9px;padding:3px">OT</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>
</body>
</html>`;
}

export default function MonthlyReportPage() {
  const companyName     = useSelector(selectCompanyName);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const now   = new Date();
  const [year, setYear]       = useState(now.getFullYear());
  const [month, setMonth]     = useState(now.getMonth() + 1);
  const [deptFilter, setDept] = useState('');

  const monthStr    = `${year}-${String(month).padStart(2, '0')}`;
  const daysInMonth = getDaysInMonth(new Date(year, month - 1));

  // Real employees — drives generated attendance data for this company
  const { data: empData }    = useEmployees({ status: 'ACTIVE', limit: 500 });
  const realEmployees        = useMemo(() => empData?.data || [], [empData]);

  // Departments first so we can derive the name for the mock filter
  const { data: deptData }   = useDepartments();
  const departments           = deptData?.data || [];
  const selectedDeptName      = departments.find(d => d.id === deptFilter)?.name || '';

  const { data, isLoading }  = useMonthlyAllAttendance(monthStr, selectedDeptName ? { department: selectedDeptName } : {}, activeCompanyId, realEmployees);
  const employees             = data?.data || [];

  const dayHeaders = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(year, month - 1, i + 1);
    return { day: i + 1, abbr: DAY_ABBR[getDay(d)], isWeekend: getDay(d) === 0 || getDay(d) === 6 };
  }), [year, month, daysInMonth]);

  const exportRows = useMemo(() => buildMonthlyGridRows(employees), [employees]);
  const filterSummary = `${MONTHS[month-1]} ${year}${selectedDeptName ? ` · ${selectedDeptName}` : ''}`;

  const handleExport = (fmt) => {
    if (fmt === 'print' || fmt === 'pdf') {
      const html = buildPrintHtml({ employees, dayHeaders, daysInMonth, year, month, companyName });
      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      if (fmt === 'print') setTimeout(() => win.print(), 500);
    } else {
      generateCustomReport({
        rows: exportRows, title: `Monthly Attendance — ${MONTHS[month-1]} ${year}`,
        filename: `monthly-${monthStr}`, format: fmt,
        companyName, filterSummary,
      });
    }
  };

  return (
    <div className="p-6 space-y-5">
      <nav className="flex items-center gap-1.5 text-[12px] text-[var(--text-tertiary)]">
        <Link to="/reports" className="hover:text-[var(--accent)]">Reports</Link>
        <ChevronRight size={12}/>
        <span className="text-[var(--text-primary)] font-medium">Monthly</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Monthly Attendance Report</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">{MONTHS[month-1]} {year} · {employees.length} employees · {companyName}</p>
        </div>
        {/* Export bar */}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => handleExport('xlsx')}
            className="flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all">
            Excel
          </button>
          <button onClick={() => handleExport('csv')}
            className="flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all">
            CSV
          </button>
          <button onClick={() => handleExport('pdf')}
            className="flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all">
            PDF
          </button>
          <button onClick={() => handleExport('web')}
            className="flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-lg text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all">
            Web
          </button>
          <button onClick={() => handleExport('print')}
            className="flex items-center gap-2 h-9 px-4 bg-[var(--accent)] text-white rounded-lg text-[13px] font-semibold hover:opacity-90 transition-opacity">
            Print A3
          </button>
        </div>
      </div>

      {/* Controls + Legend */}
      <div className="flex flex-wrap gap-3 items-center">
        <select value={month} onChange={e => setMonth(Number(e.target.value))}
          className="h-9 px-3 text-[13px] bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          className="h-9 px-3 text-[13px] bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={deptFilter} onChange={e => setDept(e.target.value)}
          className="h-9 px-3 text-[13px] bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <div className="flex items-center gap-3 ml-auto flex-wrap">
          {Object.entries(STATUS_CONFIG).map(([code, cfg]) => (
            <div key={code} className="flex items-center gap-1">
              <div className={cn('w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold', cfg.bg, cfg.text)}>{code}</div>
              <span className="text-[11px] text-[var(--text-tertiary)]">{cfg.label.split('/')[0]}</span>
            </div>
          ))}
          <span className="text-[11px] text-[var(--text-tertiary)]">· Hover cell for times · <span className="text-[var(--accent)] font-bold">N×</span> = multi-punch day</span>
        </div>
      </div>

      {/* Grid */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-visible">
        {isLoading ? (
          <div className="p-10 text-center text-[var(--text-tertiary)]">
            <div className="w-8 h-8 border-2 border-[var(--accent)]/20 border-t-[var(--accent)] rounded-full animate-spin mx-auto mb-3"/>
            Loading attendance data…
          </div>
        ) : (
          <MonthlyGrid employees={employees} dayHeaders={dayHeaders} daysInMonth={daysInMonth}/>
        )}
      </div>
    </div>
  );
}

function MonthlyGrid({ employees, dayHeaders, daysInMonth }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[11px]" style={{ minWidth: daysInMonth * 38 + 280 }}>
        <thead>
          <tr className="bg-[var(--bg-app)] border-b border-[var(--border)]">
            <th className="text-left px-3 py-2.5 font-semibold text-[var(--text-secondary)] whitespace-nowrap sticky left-0 bg-[var(--bg-app)] z-10 min-w-[220px] border-r border-[var(--border)]">
              Employee
            </th>
            {dayHeaders.map(({ day, abbr, isWeekend }) => (
              <th key={day}
                className={cn('px-1 py-2 text-center font-semibold whitespace-nowrap min-w-[36px]', isWeekend ? 'text-red-400 bg-red-50/30 dark:bg-red-900/10' : 'text-[var(--text-tertiary)]')}>
                <div className="text-[10px]">{day}</div>
                <div className="text-[9px] opacity-70">{abbr[0]}</div>
              </th>
            ))}
            {[
              { s: 'P',  cls: 'text-green-600' },
              { s: 'HP', cls: 'text-yellow-600' },
              { s: 'A',  cls: 'text-red-500' },
              { s: 'L',  cls: 'text-blue-600' },
              { s: 'H',  cls: 'text-purple-600' },
              { s: 'S',  cls: 'text-[var(--text-tertiary)]' },
              { s: 'OT', cls: 'text-[var(--accent)]' },
            ].map(({ s, cls }) => (
              <th key={s} className={cn('px-2 py-2.5 text-center font-bold whitespace-nowrap min-w-[32px] text-[11px]', cls)}>
                {s}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {employees.length === 0 ? (
            <tr>
              <td colSpan={daysInMonth + 8} className="px-4 py-10 text-center text-[var(--text-tertiary)]">
                No attendance data for this period
              </td>
            </tr>
          ) : employees.map((emp, idx) => (
            <tr key={emp.employee_id}
              className={cn('border-b border-[var(--border)] last:border-0', idx % 2 === 0 ? '' : 'bg-[var(--bg-app)]/40')}>
              <td className="px-3 py-2 sticky left-0 bg-[var(--bg-card)] z-10 border-r border-[var(--border)]">
                <div className="font-semibold text-[var(--text-primary)] leading-tight truncate max-w-[200px]">{emp.name}</div>
                <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{emp.designation || emp.department}</div>
                <div className="text-[9px] font-mono text-[var(--text-tertiary)] opacity-70">{emp.employee_code}</div>
              </td>
              {dayHeaders.map(({ day }) => (
                <td key={day} className={cn('px-0.5 py-1 text-center', dayHeaders.find(d=>d.day===day)?.isWeekend ? 'bg-red-50/20 dark:bg-red-900/5' : '')}>
                  <StatusCell
                    code={emp.days?.[String(day)] || 'S'}
                    dayTimes={emp.times?.[String(day)]}
                  />
                </td>
              ))}
              {['P','HP','A','L','H','S'].map(s => (
                <td key={s} className="px-2 py-2 text-center font-bold text-[12px]">
                  <span className={
                    s === 'P'  ? 'text-green-600'  :
                    s === 'HP' ? 'text-yellow-600' :
                    s === 'A'  ? 'text-red-500'    :
                    s === 'L'  ? 'text-blue-600'   :
                    s === 'H'  ? 'text-purple-600' :
                    'text-[var(--text-tertiary)]'
                  }>{emp.summary?.[s] ?? 0}</span>
                </td>
              ))}
              <td className="px-2 py-2 text-center text-[11px] font-semibold text-[var(--accent)] whitespace-nowrap">
                {emp.summary?.OT || '0h 0m'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
